import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { auth } from '../utils/auth';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { api } = useAppContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (auth.isLoggedIn()) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { CognitoIdentityProviderClient, InitiateAuthCommand } = await import(
        '@aws-sdk/client-cognito-identity-provider'
      );
      const { awsConfig, generateSecretHash } = await import('../aws-config');

      const clientId = awsConfig.Auth.Cognito.userPoolClientId;
      const clientSecret = awsConfig.Auth.Cognito.userPoolClientSecret;
      const region = awsConfig.Auth.Cognito.region;

      if (!clientId || !clientSecret || !region) {
        throw new Error('Admin auth environment variables are missing');
      }

      const secretHash = generateSecretHash(email, clientId, clientSecret);
      const client = new CognitoIdentityProviderClient({ region });

      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
          SECRET_HASH: secretHash
        }
      });

      const response = await client.send(command);

      if (response.AuthenticationResult) {
        const { AccessToken, IdToken, RefreshToken } = response.AuthenticationResult;
        auth.setTokens(AccessToken, IdToken, RefreshToken);

        const res = await api.getTokenValidation();
        if (res.data.s !== 'ok' || res.data.code !== 200) {
          navigate('/fyers/login', { replace: true });
          return;
        }

        navigate(location.state?.from?.pathname || '/', { replace: true });
      } else {
        setError('Authentication failed');
      }
    } catch (caughtError) {
      console.error('Admin login failed:', caughtError);

      if (caughtError.name === 'NotAuthorizedException') {
        setError('Invalid username or password');
      } else if (caughtError.message === 'Admin auth environment variables are missing') {
        setError('Admin auth config missing. Check admincode/.env and restart npm start.');
      } else {
        setError('Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-panel">
        <div className="brand-block">
          <p className="eyebrow">Scheduler Admin</p>
          <h1>Control the platform from one secure dashboard</h1>
        </div>

        <form className="login-card" onSubmit={handleLogin}>
          <h2>Admin Login</h2>
          {error ? (
            <div className="error-banner" role="alert">
              {error}
            </div>
          ) : null}
          <label>
            Username
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Enter your admin email"
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter your password"
              required
            />
          </label>
          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
